import { DepartmentsEnum, SortOrder } from '@moonpig/web-explore-types-graphql'
import { SearchEvent } from '@moonpig/web-core-analytics'
import { SearchFilter } from '../services'
import { SelectedSuggestion } from '../components/types'
import { FilteringEvent, FilterSource } from '../types'
import { SearchInputType, SearchSortByOptions } from './types'

type SearchGAEventProps = {
  correctedSearchTerm: string
  department: DepartmentsEnum[]
  filters: SearchFilter[]
  filteringEvent?: FilteringEvent
  numberOfResults: number
  searchTerm: string
  sort: SortOrder
  selectedSuggestion?: SelectedSuggestion
  isTrendingSuggestion: boolean
  showNbas: boolean
  showRudeProducts: boolean
  redirectedDepartment?: DepartmentsEnum
  redirectionSource?: 'smart_redirection' | 'no_results'
}

const maxCharLimit = 500

// GA4 event: 'search'
// https://github.com/Moonpig/moonpig-data-tracking/blob/main/events/specs/search.md
export const createSearchGAEvent = ({
  correctedSearchTerm,
  department,
  filters,
  filteringEvent,
  numberOfResults,
  searchTerm,
  sort,
  selectedSuggestion,
  isTrendingSuggestion,
  showNbas,
  showRudeProducts,
  redirectedDepartment,
  redirectionSource,
}: SearchGAEventProps): SearchEvent => {
  const filterSource =
    filteringEvent &&
    filteringEvent.source !== FilterSource.Gallery &&
    filteringEvent.source
  const selectedFilter = filteringEvent?.filter

  const getInputType = (): SearchInputType => {
    if (filterSource) {
      return filterSource
    }
    if (selectedSuggestion?.facetKey) {
      return 'facet suggestion'
    }
    if (isTrendingSuggestion) {
      return 'trending suggestion'
    }
    return 'keyword'
  }

  const getLabelText = () => {
    if (correctedSearchTerm) {
      return `${searchTerm} | ${correctedSearchTerm}`.toLowerCase()
    }
    if (searchTerm) {
      return searchTerm.toLowerCase()
    }
    if (selectedSuggestion?.facetKey) {
      return selectedSuggestion?.facetKey.toLowerCase()
    }
    return 'undefined'
  }

  const inputType = getInputType()

  return {
    event: 'search',
    event_data: {
      category: 'search',
      action: 'run search',
      label: getLabelText(),
      non_interaction: true,
      value: undefined,
    },

    results_data: {
      input_type: inputType,
      results_list:
        inputType === 'facet suggestion'
          ? selectedSuggestion?.facetKey?.toLowerCase() ||
            /* istanbul ignore next */ 'undefined'
          : searchTerm.toLowerCase(),
      corrected_search_term: correctedSearchTerm
        ? correctedSearchTerm.toLowerCase()
        : undefined,
      product_category:
        (department.length && department[0].toLowerCase()) ||
        /* istanbul ignore next */ 'n/a',
      number_of_results: numberOfResults,
      redirected_from:
        (redirectedDepartment &&
          `${redirectedDepartment.toLowerCase()} | ${redirectionSource}`) ||
        /* istanbul ignore next */ undefined,
      sort_by: sort.toLowerCase() as SearchSortByOptions,
    },

    filter_data: {
      filter_name:
        filters.length > 0
          ? filters
              .map(f => f.key)
              .join('|')
              .toLowerCase()
              .substring(0, maxCharLimit)
          : undefined, // maxCharLimit hard limit
      last_filter_interaction: filteringEvent?.action
        ? `${filteringEvent.action} | ${selectedFilter?.parent} | ${selectedFilter?.id}`.toLowerCase()
        : undefined,
      number_of_active_filters: filters.length,
      show_rude_products: showRudeProducts ? 'true' : 'false',
      show_nbas: showNbas ? 'true' : 'false',
    },
  }
}
